import { useState, useCallback } from 'react'

export enum StepStatus {
  inactive = 'INACTIVE',
  active = 'ACTIVE',
  completed = 'COMPLETED'
}

export interface StepConfig {
  [stepName: string]: StepStatus
}

export interface UseStepOptions {
  isCreate?: boolean
  createSteps?: StepConfig
  editSteps?: StepConfig
}

export interface UseStepReturn {
  stepsStatus: StepConfig
  setStepsStatus: React.Dispatch<React.SetStateAction<StepConfig>>
  updateStep: (stepName: string, status: StepStatus) => void
  updateSteps: (updates: StepConfig) => void
  activateStep: (stepName: string) => void
  completeStep: (stepName: string) => void
  deactivateStep: (stepName: string) => void
  resetSteps: () => void
  isStepActive: (stepName: string) => boolean
  isStepCompleted: (stepName: string) => boolean
  isStepInactive: (stepName: string) => boolean
}

export const useStep = ({
  isCreate = true,
  createSteps = {},
  editSteps = {}
}: UseStepOptions): UseStepReturn => {
  const initializeStepsStatus = (): StepConfig => {
    return isCreate ? createSteps : editSteps
  }

  const [stepsStatus, setStepsStatus] = useState<StepConfig>(() =>
    initializeStepsStatus()
  )

  const updateStep = useCallback((stepName: string, status: StepStatus) => {
    setStepsStatus((prev) => ({
      ...prev,
      [stepName]: status
    }))
  }, [])

  const updateSteps = useCallback((updates: StepConfig) => {
    setStepsStatus((prev) => ({
      ...prev,
      ...updates
    }))
  }, [])

  const activateStep = useCallback(
    (stepName: string) => {
      updateStep(stepName, StepStatus.active)
    },
    [updateStep]
  )

  const completeStep = useCallback(
    (stepName: string) => {
      updateStep(stepName, StepStatus.completed)
    },
    [updateStep]
  )

  const deactivateStep = useCallback(
    (stepName: string) => {
      updateStep(stepName, StepStatus.inactive)
    },
    [updateStep]
  )

  const resetSteps = useCallback(() => {
    setStepsStatus(initializeStepsStatus())
  }, [isCreate, createSteps, editSteps])

  const isStepActive = useCallback(
    (stepName: string) => {
      return stepsStatus[stepName] === StepStatus.active
    },
    [stepsStatus]
  )

  const isStepCompleted = useCallback(
    (stepName: string) => {
      return stepsStatus[stepName] === StepStatus.completed
    },
    [stepsStatus]
  )

  const isStepInactive = useCallback(
    (stepName: string) => {
      return stepsStatus[stepName] === StepStatus.inactive
    },
    [stepsStatus]
  )

  return {
    stepsStatus,
    setStepsStatus,
    updateStep,
    updateSteps,
    activateStep,
    completeStep,
    deactivateStep,
    resetSteps,
    isStepActive,
    isStepCompleted,
    isStepInactive
  }
}

// Form field configurations for different discount types
export interface BasketDiscountFormFields {
  step1: string[] // Application method and schedule
  step2: string[] // Location and fulfilment types
  step3: string[] // Discount type
  step4: string[] // Eligible products (optional for delivery)
  step5: string[] // Restrictions
  step6: string[] // Define rules
}

export interface ProductDiscountFormFields {
  step1: string[] // Application method and schedule
  step2: string[] // Location and fulfilment types
  step3: string[] // Discount type
  step4?: string[] // Eligible products (optional for delivery)
  step5: string[] // Restrictions
  step6: string[] // Define rules
}

export interface DeliveryDiscountFormFields {
  step1: string[] // Application method and schedule
  step2: string[] // Location and fulfilment types
  step3: string[] // Discount type
  step4: string[] // Restrictions
  step5: string[] // Define rules
}

export const DISCOUNT_FORM_FIELDS: {
  BASKET: BasketDiscountFormFields
  PRODUCT: ProductDiscountFormFields
  DELIVERY: DeliveryDiscountFormFields
} = {
  BASKET: {
    step1: ['trigger', 'code'],
    step2: ['store_ids', 'fulfillment_types'],
    step3: [
      'type',
      'discount_percentage',
      'discount_fixed',
      'discount_buy_one_special'
    ],
    step4: ['target'],
    step5: ['quantity', 'quantity_per_customer', 'minimum_value'],
    step6: ['value_limit', 'product_limit']
  },
  PRODUCT: {
    step1: ['trigger', 'code'],
    step2: ['store_ids', 'fulfillment_types'],
    step3: [
      'type',
      'discount_percentage',
      'discount_fixed',
      'discount_buy_one_special'
    ],
    step4: ['target', 'category_ids', 'product_ids'],
    step5: ['quantity', 'quantity_per_customer', 'minimum_value'],
    step6: ['value_limit', 'product_limit']
  },
  DELIVERY: {
    step1: ['trigger', 'code'],
    step2: ['store_ids', 'fulfillment_types'],
    step3: [
      'type',
      'discount_percentage',
      'discount_fixed',
      'discount_buy_one_special'
    ],
    step4: ['quantity', 'quantity_per_customer'],
    step5: ['minimum_value']
  }
}

// Common step configurations for reuse
export const COMMON_STEP_CONFIGS = {
  BASKET_DISCOUNT_STEPS: {
    create: {
      APPLICATION_METHOD: StepStatus.active,
      LOCATION_AND_FULFILMENT_TYPES: StepStatus.inactive,
      DISCOUNT_TYPE: StepStatus.inactive,
      BASKET_COMPONENTS: StepStatus.inactive,
      RESTRICTIONS: StepStatus.inactive,
      DEFINE_RULES: StepStatus.inactive
    },
    edit: {
      APPLICATION_METHOD: StepStatus.completed,
      LOCATION_AND_FULFILMENT_TYPES: StepStatus.completed,
      DISCOUNT_TYPE: StepStatus.completed,
      BASKET_COMPONENTS: StepStatus.completed,
      RESTRICTIONS: StepStatus.completed,
      DEFINE_RULES: StepStatus.completed
    }
  },
  PRODUCT_DISCOUNT_STEPS: {
    create: {
      APPLICATION_METHOD: StepStatus.active,
      LOCATION_AND_FULFILMENT_TYPES: StepStatus.inactive,
      DISCOUNT_TYPE: StepStatus.inactive,
      ELIGIBLE_PRODUCTS: StepStatus.inactive,
      RESTRICTIONS: StepStatus.inactive,
      DEFINE_RULES: StepStatus.inactive
    },
    edit: {
      APPLICATION_METHOD: StepStatus.completed,
      LOCATION_AND_FULFILMENT_TYPES: StepStatus.completed,
      DISCOUNT_TYPE: StepStatus.completed,
      ELIGIBLE_PRODUCTS: StepStatus.completed,
      RESTRICTIONS: StepStatus.completed,
      DEFINE_RULES: StepStatus.completed
    }
  },
  DELIVERY_DISCOUNT_STEPS: {
    create: {
      APPLICATION_METHOD: StepStatus.active,
      LOCATIONS: StepStatus.inactive,
      DISCOUNT_TYPE: StepStatus.inactive,
      RESTRICTIONS: StepStatus.inactive,
      DEFINE_RULES: StepStatus.inactive
    },
    edit: {
      APPLICATION_METHOD: StepStatus.completed,
      LOCATIONS: StepStatus.completed,
      DISCOUNT_TYPE: StepStatus.completed,
      RESTRICTIONS: StepStatus.completed,
      DEFINE_RULES: StepStatus.completed
    }
  },
  CATEGORY_CUT_OFF_STEPS: {
    create: {
      CHOOSE_CATEGORIES: StepStatus.active,
      SCHEDULE: StepStatus.inactive,
      CONFIGURE_CUT_OFF_TIMES: StepStatus.inactive
    },
    edit: {
      CHOOSE_CATEGORIES: StepStatus.completed,
      SCHEDULE: StepStatus.completed,
      CONFIGURE_CUT_OFF_TIMES: StepStatus.completed
    }
  },
  SPECIAL_AVAILABILITY_STEPS: {
    create: {
      CHOOSE_CATEGORY: StepStatus.active,
      CHOOSE_INVENTORY: StepStatus.inactive,
      SCHEDULE: StepStatus.inactive
    },
    edit: {
      CHOOSE_CATEGORY: StepStatus.completed,
      CHOOSE_INVENTORY: StepStatus.completed,
      SCHEDULE: StepStatus.completed
    }
  }
}
